import { request, PageResult } from './request';

/**
 * 平台访问申请/角色管理 (2026-08-25, 后端 material-library internal/app/access, /v1/access/*).
 * passport 登录 ≠ 可访问: 非管理员须申请, 生成审批中心工单, 管理员在审批中心通过/驳回;
 * 数据表为 rbac_user (仅用户与准入状态; 管理员/普通用户是 rbac_role_binding 里的 admin/user 角色绑定), AccessGate 为全局闸门.
 */

export type AccessStatus = 'none' | 'pending' | 'approved' | 'rejected';

export interface MyAccessInfo {
    status: AccessStatus;
    reason: string;
    appliedAt: number;
    reviewedAt: number;
}

export interface AccessUserRow {
    id: number;
    uid: number;
    email: string;
    displayName?: string;
    status: AccessStatus;
    reason: string;
    reviewedBy: number;
    reviewedByName: string;
    reviewedAt: number;
    createdAt: number;
}

export interface ListAccessUsersParams {
    status?: string;
    keyword?: string;
    page?: number;
    pageSize?: number;
}

const BASE = '/v1/access';

/** 当前用户的申请状态 (免访问授权). */
export function fetchMyAccess(): Promise<MyAccessInfo> {
    return request<MyAccessInfo>(`${BASE}/me`);
}

/** 申请页可勾选的角色 (免访问授权): 全部非 admin 角色, user 恒在 (前端默认勾且不可取消). */
export interface ApplicableRole {
    name: string;
    displayName: string;
    description: string;
    /** 预置资源角色 (<资源>-viewer / <资源>-admin) 才有: 按资源分组渲染「只读 / 管理」两档. */
    resource?: string;
    tier?: 'viewer' | 'admin' | 'module';
    resourceLabel?: string;
    group?: string;
    sensitive?: boolean;
}
export function fetchApplicableRoles(): Promise<{ list: ApplicableRole[] }> {
    return request(`${BASE}/roles`);
}

/** 提交使用申请 (免访问授权). roles 为勾选的资源管理员角色 (普通用户 user 隐含, 不必传). */
export function applyAccess(
    reason: string,
    roles: string[] = [],
): Promise<null> {
    return request(`${BASE}/apply`, {
        method: 'POST',
        body: { reason, roles },
    });
}

/** 访问名单 (admin). */
export function listAccessUsers(
    params: ListAccessUsersParams,
): Promise<PageResult<AccessUserRow>> {
    return request<PageResult<AccessUserRow>>(`${BASE}/users`, {
        query: params,
    });
}

/** 普通用户 → 管理员 (admin, 直接操作不走审批). */
export function promoteUser(uid: number): Promise<null> {
    return request(`${BASE}/promote`, { method: 'POST', body: { uid } });
}

/** 管理员 → 普通用户 (admin; 行保持 approved, 降级后仍可访问). */
export function demoteAdmin(email: string): Promise<null> {
    return request(`${BASE}/demote`, { method: 'POST', body: { email } });
}

/** 移除访问授权 (admin); 被移除的用户可重新申请. */
export function revokeAccess(uid: number): Promise<null> {
    return request(`${BASE}/revoke`, { method: 'POST', body: { uid } });
}
